import type { DriveStep } from 'driver.js';

const at = (id: string) => `[data-tour="${id}"]`;

/**
 * The dashboard walkthrough — the ONE tour that auto-starts (once, on a user's first visit).
 * It tours the dashboard's OWN content first (what's right in front of them), then maps out the
 * sidebar's sections. Each destination page also has its own "Tour this page" button for a closer
 * look, so this stays a concise welcome rather than a deep dive into every page.
 *
 * Anchors: dash-* live in DashboardPage.tsx; sidebar / studio / content / golive / account /
 * tour-trigger live in AgentLayout.tsx (the sidebar group headers + topbar button, always visible
 * on desktop). On mobile the sidebar is behind the hamburger, so those steps are simply skipped
 * (runTour drops anchors that aren't in the DOM).
 */
export function getDashboardTourSteps(): DriveStep[] {
  return [
    {
      popover: {
        title: '👋 Welcome to your dashboard',
        description:
          "Here's a quick tour of your dashboard and how your menu is organized. You can skip " +
          'anytime, and replay it later from the “Take a tour” button.',
      },
    },
    {
      element: at('dash-ai-prompt'),
      popover: {
        title: 'Build with AI',
        description:
          'The fastest way to start: describe the site you want in plain English (or tap the mic) ' +
          'and the AI builds it for you.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: at('dash-template-gallery'),
      popover: {
        title: 'Or start from a template',
        description: 'Prefer a head start? Pick a ready-made template and make it your own.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: at('dash-stats'),
      popover: {
        title: 'Track your results',
        description:
          'Once your site is live, your visits, new leads, and top-performing page show up here at a ' +
          'glance.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: at('dash-recent-leads'),
      popover: {
        title: 'Follow up with leads',
        description: 'New inquiries from your site land here so you can reach out fast.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: at('sidebar'),
      popover: {
        title: 'Your menu',
        description: 'Everything lives in this menu, grouped into sections. Here’s a quick map.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('studio'),
      popover: {
        title: 'My Website',
        description: 'Open the builder anytime to create and edit your pages with AI.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('content'),
      popover: {
        title: 'Website Content',
        description:
          'Your brand, listings, blog, communities, offices, and agents all live in here.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('golive'),
      popover: {
        title: 'Go Live',
        description: 'Publish your site, set up your domain, and preview — all under Go Live.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('account'),
      popover: {
        title: 'My Account',
        description: 'Your team, AI usage, and billing live under My Account.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('tour-trigger'),
      popover: {
        title: "That's the tour! 🎉",
        description:
          'Replay this anytime from here. And every page has its own “Tour this page” button for a ' +
          'closer look at that page’s features.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];
}
